import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input, Label, Textarea } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast, Toaster } from "sonner";
import { ArrowLeft, Plus, Check, X, FileText, ClipboardCheck, AlertTriangle, Users, Trash2, Printer } from "lucide-react";
import { format } from "date-fns";
import { cn } from "../lib/utils";

const statusMap = {
  planned: { v: "neutral", label: "Planned" },
  in_progress: { v: "info", label: "In progress" },
  review: { v: "warning", label: "Review" },
  completed: { v: "success", label: "Completed" },
  cancelled: { v: "neutral", label: "Cancelled" },
};
const sevMap = {
  critical: "critical", high: "warning", medium: "warning", low: "success", informational: "info"
};

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "checklist", label: "Checklist" },
  { key: "observations", label: "Observations" },
  { key: "findings", label: "Findings" },
  { key: "timeline", label: "Timeline" },
  { key: "report", label: "Report" },
];

export default function AuditDetailPage() {
  const { id } = useParams();
  const [a, setA] = useState(null);
  const [users, setUsers] = useState([]);
  const [tab, setTab] = useState("overview");
  const [showAssign, setShowAssign] = useState(false);
  const [assignForm, setAssignForm] = useState({ lead_auditor_id: "", auditor_ids: [] });
  const [newItem, setNewItem] = useState({ item: "", description: "" });
  const [newObs, setNewObs] = useState({ title: "", description: "", severity: "medium" });
  const [newFind, setNewFind] = useState({ title: "", description: "", severity: "medium" });
  const [timeline, setTimeline] = useState([]);
  const [report, setReport] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/audits/${id}`);
    setA(data);
  };
  useEffect(() => {
    load();
    (async () => {
      try {
        const { data } = await api.get("/users?limit=200");
        setUsers(data.items || []);
      } catch (e) {
        console.error("Failed to load users list", e);
      }
    })();
  }, [id]);

  useEffect(() => {
    if (tab === "timeline" && a) {
      api.get(`/audits/${id}/timeline`).then(({ data }) => setTimeline(data.items || []));
    }
    if (tab === "report" && a) {
      api.get(`/audits/${id}/report`).then(({ data }) => setReport(data));
    }
  }, [tab, a, id]);

  if (!a) return <div className="h-40 bg-muted animate-pulse rounded" />;
  const s = statusMap[a.status] || { v: "neutral", label: a.status };

  const addChecklist = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/audits/${id}/checklist`, newItem);
      setNewItem({ item: "", description: "" });
      load();
    } catch (err) { toast.error("Failed to add item"); }
  };
  const toggleChecklist = async (item, status) => {
    await api.patch(`/audits/${id}/checklist/${item.id}`, { status });
    load();
  };
  const delChecklist = async (item) => {
    if (!window.confirm("Remove this checklist item?")) return;
    await api.delete(`/audits/${id}/checklist/${item.id}`);
    load();
  };
  const addObs = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/audits/${id}/observations`, newObs);
      setNewObs({ title: "", description: "", severity: "medium" });
      load();
    } catch (err) { toast.error("Failed"); }
  };
  const addFind = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/audits/${id}/findings`, newFind);
      setNewFind({ title: "", description: "", severity: "medium" });
      load();
      toast.success("Finding raised — remediation task auto-created for critical/high");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed"); }
  };
  const submitAssign = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/audits/${id}/assign`, assignForm);
      toast.success("Auditors assigned");
      setShowAssign(false);
      load();
    } catch (err) { toast.error("Failed"); }
  };
  const openAssign = () => {
    setAssignForm({
      lead_auditor_id: a.lead_auditor_id || "",
      auditor_ids: a.auditor_ids || [],
    });
    setShowAssign(true);
  };

  return (
    <div className="space-y-6">
      <Toaster richColors position="top-right" />

      <Link to="/audits" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="w-4 h-4" /> All audits
      </Link>

      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-start gap-4">
            <div className="w-12 h-12 rounded-md bg-primary text-primary-foreground flex items-center justify-center shrink-0">
              <ClipboardCheck className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Badge variant="primary">{a.framework}</Badge>
                <Badge variant={s.v}>{s.label}</Badge>
              </div>
              <h1 className="text-2xl font-semibold tracking-tight">{a.title}</h1>
              {a.scope && <p className="text-sm text-muted-foreground mt-2">{a.scope}</p>}
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <Stat label="Lead auditor" value={a.lead_auditor_name || "—"} />
                <Stat label="Auditors" value={a.auditor_details?.map((x) => x.name).join(", ") || "—"} />
                <Stat label="Start" value={a.start_date ? format(new Date(a.start_date), "MMM d, yyyy") : "—"} />
                <Stat label="Due" value={a.due_date ? format(new Date(a.due_date), "MMM d, yyyy") : "—"} />
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="text-3xl font-semibold text-primary tabular-nums">{a.progress || 0}%</div>
              <div className="text-xs text-muted-foreground">
                {a.checklist_completed || 0} / {a.checklist_count || 0} checklist done
              </div>
              <Button size="sm" variant="outline" onClick={openAssign} data-testid="audit-assign-btn">
                <Users className="w-4 h-4" /> Assign auditors
              </Button>
            </div>
          </div>
          <div className="mt-4 h-2 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-primary" style={{ width: `${a.progress || 0}%` }} />
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-wrap gap-1 border-b border-border">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`audit-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={cn(
              "px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors",
              tab === t.key
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard title="Checklist" icon={ClipboardCheck} value={`${a.checklist_completed || 0}/${a.checklist_count || 0}`} />
          <StatCard title="Observations" icon={FileText} value={a.observations_count || 0} />
          <StatCard title="Findings" icon={AlertTriangle} value={a.findings_count || 0} />
          {a.objectives && (
            <Card className="md:col-span-3">
              <CardHeader><CardTitle>Objectives</CardTitle></CardHeader>
              <CardContent className="whitespace-pre-line text-sm text-muted-foreground">{a.objectives}</CardContent>
            </Card>
          )}
          {a.methodology && (
            <Card className="md:col-span-3">
              <CardHeader><CardTitle>Methodology</CardTitle></CardHeader>
              <CardContent className="whitespace-pre-line text-sm text-muted-foreground">{a.methodology}</CardContent>
            </Card>
          )}
        </div>
      )}

      {tab === "checklist" && (
        <Card>
          <CardHeader><CardTitle>Checklist</CardTitle><CardDescription>Progressively complete audit steps.</CardDescription></CardHeader>
          <CardContent>
            <form onSubmit={addChecklist} className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-4">
              <Input data-testid="checklist-input" placeholder="Checklist item…" value={newItem.item} onChange={(e) => setNewItem({ ...newItem, item: e.target.value })} required />
              <Input placeholder="Description (optional)" value={newItem.description} onChange={(e) => setNewItem({ ...newItem, description: e.target.value })} />
              <Button type="submit" data-testid="checklist-add"><Plus className="w-4 h-4" /> Add</Button>
            </form>
            <ul className="divide-y divide-border">
              {(a.checklist || []).length === 0 && <li className="py-6 text-center text-sm text-muted-foreground">No items yet.</li>}
              {(a.checklist || []).map((c) => (
                <li key={c.id} data-testid={`checklist-item-${c.id}`} className="flex items-center gap-3 py-3">
                  <button
                    onClick={() => toggleChecklist(c, c.status === "completed" ? "pending" : "completed")}
                    className={cn("w-5 h-5 rounded border flex items-center justify-center transition-colors",
                                 c.status === "completed" ? "bg-green-600 border-green-600 text-white" : "border-border hover:border-primary")}
                    data-testid={`checklist-toggle-${c.id}`}
                  >
                    {c.status === "completed" && <Check className="w-3 h-3" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className={cn("text-sm font-medium", c.status === "completed" && "line-through text-muted-foreground")}>{c.item}</div>
                    {c.description && <div className="text-xs text-muted-foreground">{c.description}</div>}
                  </div>
                  <Badge variant={c.status === "completed" ? "success" : c.status === "in_progress" ? "info" : "neutral"}>
                    {c.status.replace("_", " ")}
                  </Badge>
                  <Button size="icon" variant="destructive" onClick={() => delChecklist(c)}><Trash2 className="w-4 h-4" /></Button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {tab === "observations" && (
        <Card>
          <CardHeader><CardTitle>Observations</CardTitle><CardDescription>Neutral field observations; graduate to a finding when material.</CardDescription></CardHeader>
          <CardContent>
            <form onSubmit={addObs} className="grid grid-cols-1 md:grid-cols-6 gap-2 mb-4">
              <Input data-testid="obs-title" className="md:col-span-2" placeholder="Title…" value={newObs.title} onChange={(e) => setNewObs({ ...newObs, title: e.target.value })} required />
              <Input className="md:col-span-2" placeholder="Description" value={newObs.description} onChange={(e) => setNewObs({ ...newObs, description: e.target.value })} />
              <select value={newObs.severity} onChange={(e) => setNewObs({ ...newObs, severity: e.target.value })} className="h-10 rounded-md border border-border bg-card px-3 text-sm">
                {["informational", "low", "medium", "high", "critical"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <Button type="submit" data-testid="obs-add"><Plus className="w-4 h-4" /> Add</Button>
            </form>
            <ul className="divide-y divide-border">
              {(a.observations || []).length === 0 && <li className="py-6 text-center text-sm text-muted-foreground">No observations yet.</li>}
              {(a.observations || []).map((o) => (
                <li key={o.id} className="py-3">
                  <div className="flex items-center gap-2">
                    <div className="font-medium text-sm">{o.title}</div>
                    <Badge variant={sevMap[o.severity]}>{o.severity}</Badge>
                  </div>
                  {o.description && <div className="text-sm text-muted-foreground mt-1">{o.description}</div>}
                  <div className="text-xs text-muted-foreground mt-1">
                    {o.observed_by_name} · {o.created_at && format(new Date(o.created_at), "MMM d, yyyy HH:mm")}
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {tab === "findings" && (
        <Card>
          <CardHeader>
            <CardTitle>Findings</CardTitle>
            <CardDescription>Formal audit findings. Critical/High findings auto-generate remediation tasks.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={addFind} className="grid grid-cols-1 md:grid-cols-6 gap-2 mb-4">
              <Input data-testid="find-title" className="md:col-span-2" placeholder="Finding title…" value={newFind.title} onChange={(e) => setNewFind({ ...newFind, title: e.target.value })} required />
              <Input className="md:col-span-2" placeholder="Description" value={newFind.description} onChange={(e) => setNewFind({ ...newFind, description: e.target.value })} />
              <select value={newFind.severity} onChange={(e) => setNewFind({ ...newFind, severity: e.target.value })} className="h-10 rounded-md border border-border bg-card px-3 text-sm">
                {["low", "medium", "high", "critical"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <Button type="submit" data-testid="find-add"><Plus className="w-4 h-4" /> Raise</Button>
            </form>
            <ul className="divide-y divide-border">
              {(a.findings || []).length === 0 && <li className="py-6 text-center text-sm text-muted-foreground">No findings raised.</li>}
              {(a.findings || []).map((f) => (
                <li key={f.id} className="py-3">
                  <div className="flex items-center gap-2">
                    <div className="font-medium text-sm">{f.title}</div>
                    <Badge variant={sevMap[f.severity]}>{f.severity}</Badge>
                    <Badge variant={f.status === "resolved" ? "success" : "warning"}>{f.status}</Badge>
                  </div>
                  {f.description && <div className="text-sm text-muted-foreground mt-1">{f.description}</div>}
                  <div className="text-xs text-muted-foreground mt-1">Owner: {f.owner_name || "—"}</div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {tab === "timeline" && (
        <Card>
          <CardHeader><CardTitle>Audit timeline</CardTitle><CardDescription>Chronological view of all events for this audit.</CardDescription></CardHeader>
          <CardContent>
            <ol className="relative border-l border-border ml-3 space-y-4">
              {timeline.length === 0 && <li className="text-sm text-muted-foreground">No events.</li>}
              {timeline.map((e) => (
                <li key={`${e.at}-${e.kind}-${e.title}`} className="ml-4">
                  <span className="absolute -left-2 w-4 h-4 rounded-full border border-border bg-primary" />
                  <div className="text-sm font-medium">{e.title}</div>
                  <div className="text-xs text-muted-foreground">{e.detail}</div>
                  <div className="text-xs text-muted-foreground">{format(new Date(e.at), "MMM d, yyyy HH:mm")}</div>
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>
      )}

      {tab === "report" && report && (
        <div className="space-y-4 print:space-y-2">
          <div className="flex justify-end print:hidden">
            <Button variant="outline" onClick={() => window.print()} data-testid="audit-report-print">
              <Printer className="w-4 h-4" /> Print / PDF
            </Button>
          </div>
          <Card>
            <CardHeader>
              <CardTitle>{report.title} — Audit Report</CardTitle>
              <CardDescription>
                Framework: {report.framework} · Generated {format(new Date(report.report_generated_at), "MMM d, yyyy HH:mm")} by {report.report_generated_by}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6 text-sm">
              <ReportSection title="Scope" body={report.scope} />
              <ReportSection title="Objectives" body={report.objectives} />
              <ReportSection title="Methodology" body={report.methodology} />
              <div>
                <div className="label-eyebrow mb-2">Executive summary</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <MiniStat label="Checklist" value={`${report.summary.checklist_completed}/${report.summary.checklist_total}`} />
                  <MiniStat label="Observations" value={report.summary.observations_total} />
                  <MiniStat label="Findings" value={report.summary.findings_total} />
                  <MiniStat label="Progress" value={`${report.progress || 0}%`} />
                </div>
              </div>
              <div>
                <div className="label-eyebrow mb-2">Findings by severity</div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(report.summary.findings_by_severity).map(([k, v]) => (
                    <Badge key={k} variant={sevMap[k]}>{k}: {v}</Badge>
                  ))}
                </div>
              </div>
              <div>
                <div className="label-eyebrow mb-2">Detailed findings</div>
                <ol className="space-y-3 pl-5 list-decimal">
                  {(report.findings || []).map((f) => (
                    <li key={f.id}>
                      <div className="font-medium">{f.title} <Badge variant={sevMap[f.severity]}>{f.severity}</Badge></div>
                      {f.description && <div className="text-muted-foreground text-xs mt-1">{f.description}</div>}
                    </li>
                  ))}
                  {report.findings.length === 0 && <div className="text-muted-foreground text-xs">No findings.</div>}
                </ol>
              </div>
              <div>
                <div className="label-eyebrow mb-2">Observations</div>
                <ul className="space-y-2">
                  {(report.observations || []).map((o) => (
                    <li key={o.id} className="text-xs">
                      <span className="font-medium">{o.title}</span> — <Badge variant={sevMap[o.severity]}>{o.severity}</Badge>
                    </li>
                  ))}
                  {report.observations.length === 0 && <div className="text-muted-foreground text-xs">No observations.</div>}
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {showAssign && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowAssign(false)}>
          <div className="w-full max-w-md card-surface animate-fade-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="text-base font-semibold">Assign auditors</h3>
              <button onClick={() => setShowAssign(false)} className="p-1 rounded hover:bg-muted"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submitAssign} className="p-6 space-y-4">
              <div>
                <Label>Lead auditor</Label>
                <select value={assignForm.lead_auditor_id} onChange={(e) => setAssignForm({ ...assignForm, lead_auditor_id: e.target.value })}
                        className="h-10 w-full rounded-md border border-border bg-card px-3 text-sm mt-1.5">
                  <option value="">None</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
              <div>
                <Label>Additional auditors</Label>
                <select multiple value={assignForm.auditor_ids}
                        onChange={(e) => setAssignForm({ ...assignForm, auditor_ids: Array.from(e.target.selectedOptions).map((o) => o.value) })}
                        className="mt-1.5 w-full rounded-md border border-border bg-card px-3 text-sm min-h-[120px]">
                  {users.map((u) => <option key={u.id} value={u.id}>{u.name} — {u.role}</option>)}
                </select>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowAssign(false)}>Cancel</Button>
                <Button type="submit" data-testid="audit-assign-submit">Save</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="label-eyebrow">{label}</div>
      <div className="mt-1 text-sm">{value}</div>
    </div>
  );
}
function StatCard({ title, value, icon: Icon }) {
  return (
    <Card><CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="label-eyebrow">{title}</div>
          <div className="text-2xl font-semibold mt-2 tabular-nums">{value}</div>
        </div>
        <div className="w-10 h-10 rounded-md bg-primary-subtle text-primary dark:bg-primary/15 flex items-center justify-center"><Icon className="w-5 h-5" /></div>
      </div>
    </CardContent></Card>
  );
}
function MiniStat({ label, value }) {
  return (
    <div className="rounded-md border border-border p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}
function ReportSection({ title, body }) {
  if (!body) return null;
  return (
    <div>
      <div className="label-eyebrow mb-1">{title}</div>
      <div className="whitespace-pre-line text-muted-foreground">{body}</div>
    </div>
  );
}
